import React, { useState, useEffect } from 'react';
import { Search, ChevronLeft, ChevronRight } from 'lucide-react';
import { useStatut } from '../../hooks/useStatut';
import StatutTable from './StatutTable';
import StatutForm from './StatutForm';
import Modal from '../Modal';
import ConfirmationModal from '../ConfirmationModal';

const StatutTab = ({ triggerAdd = 0, triggerRefresh = 0 }) => {
  const {
    statuts,
    loading,
    submitting,
    deleteLoading,
    searchTerm,
    pagination,
    handleSearch,
    handlePageChange,
    createStatut,
    updateStatut,
    deleteStatut,
    fetchStatuts
  } = useStatut();

  // États pour les modals
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedStatut, setSelectedStatut] = useState(null);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [statutToDelete, setStatutToDelete] = useState(null);

  // Réagir au déclencheur d'ajout depuis le bouton principal
  useEffect(() => {
    if (triggerAdd > 0) {
      handleAdd();
    }
  }, [triggerAdd]);

  // Réagir au déclencheur de rafraîchissement
  useEffect(() => {
    if (triggerRefresh > 0) {
      fetchStatuts();
    }
  }, [triggerRefresh, fetchStatuts]);

  // Gestion des actions
  const handleAdd = () => {
    setSelectedStatut(null);
    setIsModalOpen(true);
  };

  const handleEdit = (statut) => {
    setSelectedStatut(statut);
    setIsModalOpen(true);
  };

  const handleDelete = (statut) => {
    setStatutToDelete(statut);
    setIsConfirmModalOpen(true);
  };

  const handleSubmit = async (formData) => {
    const result = selectedStatut 
      ? await updateStatut(selectedStatut.id, formData)
      : await createStatut(formData);

    if (result.success) {
      setIsModalOpen(false);
      setSelectedStatut(null);
    }
  };

  const handleConfirmDelete = async () => {
    if (statutToDelete) {
      const result = await deleteStatut(statutToDelete.id);
      if (result.success) {
        setIsConfirmModalOpen(false);
        setStatutToDelete(null);
      }
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedStatut(null);
  };

  const handleCloseConfirmModal = () => {
    setIsConfirmModalOpen(false);
    setStatutToDelete(null);
  };

  // Composant de pagination
  const PaginationComponent = () => {
    if (pagination.total_pages <= 1) return null;

    return (
      <div className="flex items-center justify-between bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
        <div className="flex-1 flex justify-between sm:hidden">
          <button
            onClick={() => handlePageChange(pagination.current_page - 1)}
            disabled={!pagination.has_previous}
            className="relative inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Précédent
          </button>
          <button
            onClick={() => handlePageChange(pagination.current_page + 1)}
            disabled={!pagination.has_next}
            className="relative ml-3 inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Suivant
          </button>
        </div>
        <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
          <div>
            <p className="text-sm text-gray-700">
              Affichage de{' '}
              <span className="font-medium">
                {((pagination.current_page - 1) * pagination.page_size) + 1}
              </span>{' '}
              à{' '}
              <span className="font-medium">
                {Math.min(pagination.current_page * pagination.page_size, pagination.count)}
              </span>{' '}
              sur{' '}
              <span className="font-medium">{pagination.count}</span> résultats
            </p>
          </div>
          <div>
            <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
              <button
                onClick={() => handlePageChange(pagination.current_page - 1)}
                disabled={!pagination.has_previous}
                className="relative inline-flex items-center px-2 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-l-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              
              {/* Numéros de page */}
              {[...Array(pagination.total_pages)].map((_, index) => {
                const pageNum = index + 1;
                const isCurrentPage = pageNum === pagination.current_page;
                
                return (
                  <button
                    key={pageNum}
                    onClick={() => handlePageChange(pageNum)}
                    className={`relative inline-flex items-center px-4 py-2 text-sm font-medium border ${
                      isCurrentPage
                        ? 'z-10 bg-indigo-50 border-indigo-500 text-indigo-600'
                        : 'text-gray-700 bg-white border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
              
              <button
                onClick={() => handlePageChange(pagination.current_page + 1)}
                disabled={!pagination.has_next}
                className="relative inline-flex items-center px-2 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-r-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </nav>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="space-y-4">
        {/* Barre de recherche */}
        <div className="flex justify-between items-center">
          <div className="relative rounded-md shadow-sm w-80">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Rechercher un statut..."
              className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md"
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
            />
          </div>
        </div>

        {/* Tableau des statuts */}
        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          <StatutTable
            statuts={statuts}
            loading={loading}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onAdd={handleAdd}
            searchTerm={searchTerm}
          />
          
          {/* Pagination */}
          <PaginationComponent />
        </div>
      </div>

      {/* Modal du formulaire de statut */}
      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={selectedStatut ? 'Modifier le statut' : 'Nouveau statut'}
      >
        <StatutForm
          initialData={selectedStatut}
          onSubmit={handleSubmit}
          submitting={submitting}
          onCancel={handleCloseModal}
        />
      </Modal>

      {/* Modal de confirmation de suppression */}
      <ConfirmationModal 
        isOpen={isConfirmModalOpen}
        onClose={handleCloseConfirmModal}
        onConfirm={handleConfirmDelete}
        title="Supprimer le statut"
        message={`Êtes-vous sûr de vouloir supprimer le statut "${statutToDelete?.libelle}" ?`}
        confirmText="Supprimer"
        cancelText="Annuler"
        type="danger"
        isLoading={deleteLoading}
      />
    </>
  );
};

export default StatutTab; 